import type { ownUsageSchema } from "@rosc/contracts";
import type { z } from "zod";
import { PlanName } from "@/common/enums/enums";

type OwnUsage = z.output<typeof ownUsageSchema>;

// What the v3.2 Subscription panel, the Settings header and the
// limit-reached sheet show about the plan. Pure, so it is tested alone.

// Stripe subscription statuses (the API stores Stripe's status as is; the
// web enum also has the older "cancelled" spelling).
export type PlanStatusKind =
  | "active"
  | "trialing"
  | "pastDue"
  | "unpaid"
  | "incomplete"
  | "cancelled"
  | "paused"
  | "unknown";

export const planStatusKind = (status?: string | null): PlanStatusKind => {
  switch ((status ?? "").trim().toLowerCase()) {
    case "active":
      return "active";
    case "trialing":
      return "trialing";
    case "past_due":
      return "pastDue";
    case "unpaid":
      return "unpaid";
    case "incomplete":
    case "incomplete_expired":
      return "incomplete";
    case "canceled":
    case "cancelled":
      return "cancelled";
    case "paused":
      return "paused";
    default:
      return "unknown";
  }
};

// Chip colour: green when all is well, amber when payment needs attention,
// neutral otherwise.
export const planStatusTone = (
  kind: PlanStatusKind,
): "ok" | "warn" | "neutral" => {
  if (kind === "active" || kind === "trialing") return "ok";
  if (kind === "pastDue" || kind === "unpaid" || kind === "incomplete")
    return "warn";
  return "neutral";
};

export const isFreePlan = (planName?: string | null): boolean =>
  !planName || planName === PlanName.FREE_TRIAL.toString();

export interface PlanUserData {
  planName?: string | null;
  planStatus?: string | null;
  // Seconds since the epoch.
  periodEnd?: number | null;
  periodEndCancel?: boolean | number | null;
}

// The line next to the plan: when the free minutes reset, or when a paid
// plan renews or ends.
export type PlanDateLine = {
  kind: "resets" | "renews" | "ends" | "ended";
  date: Date;
} | null;

export const planDateLine = (
  user: PlanUserData | null | undefined,
  now: Date = new Date(),
): PlanDateLine => {
  if (!user?.periodEnd) return null;
  const date = new Date(user.periodEnd * 1000);
  if (Number.isNaN(date.getTime())) return null;
  if (planStatusKind(user.planStatus) === "cancelled" || date < now)
    return { kind: "ended", date };
  if (user.periodEndCancel) return { kind: "ends", date };
  if (isFreePlan(user.planName)) return { kind: "resets", date };
  return { kind: "renews", date };
};

export interface MinutesSummary {
  usedMinutes: number;
  // Monthly allowance of the plan (free plan only).
  allowanceMinutes: number;
  // Bought or granted minutes on top of the plan.
  extraMinutes: number;
  // Minutes that can still be recorded (plan + extra).
  leftMinutes: number;
  // 0–1 of the allowance used, for the meter.
  usedShare: number;
  periodStart: Date | null;
  periodEnd: Date | null;
}

const minutes = (seconds: number): number => Math.floor(seconds / 60);
const dateOrNull = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Same arithmetic as the sidebar's "135 min left" (AccountMenu) and the
// recording check (useSubscriptionValidation).
export const minutesSummary = (usage: OwnUsage): MinutesSummary => {
  const allowanceMinutes = minutes(usage.subscription.allowanceSeconds);
  const usedMinutes = minutes(usage.subscription.usedSeconds);
  const extraMinutes = minutes(usage.additional.availableSeconds);
  const leftMinutes =
    minutes(usage.subscription.availableSeconds) + extraMinutes;
  const total = allowanceMinutes + extraMinutes;
  return {
    usedMinutes,
    allowanceMinutes,
    extraMinutes,
    leftMinutes,
    usedShare: total > 0 ? Math.min(1, usedMinutes / total) : 1,
    periodStart: dateOrNull(usage.subscription.periodStart),
    periodEnd: dateOrNull(usage.subscription.periodEnd),
  };
};

// "November 20, 2026" in the interface language.
export const formatPlanDate = (date: Date, language: string): string =>
  date.toLocaleDateString(language || "en", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

// Why recording is blocked, for the limit-reached sheet.
export type LimitReason = "minutes" | "plan";
